import './ShotListTable.css';
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from '@nxtvid/component-library'
import { addIdentifierToShots } from '../Pages/ShotListBuilder/agent_run_functions/addIdentifierToShots';

const ShotListTable = ({ shotListState, setShotListState, shotListHistory, setShotListHistory, historyIndex, setHistoryIndex }) => {
  
  const plusIcon = require('../Images/plus-icon.png');
  const minusIcon = require('../Images/minus-icon.png');
  
  
  const MAX_HISTORY_LENGTH = 50;

  

  // Let's store dynamic widths for each column in an array
  // Default to 150px for each column if you like
  const initialFields = shotListState[0] ? Object.keys(shotListState[0]) : [];
  const [columnWidths, setColumnWidths] = useState(
    initialFields.map(() => 150)
  );

  // Let's store dynamic heights for each row
  const [rowHeights, setRowHeights] = useState(
    shotListState.map(() => 40) // default row height
  );

  // Refs to track current resizing column
  const resizingColIndexRef = useRef(null);
  const startXRef = useRef(null);
  const startWidthRef = useRef(null);


  // Debounce function to limit the rate at which a function can fire
const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  /*********************************
   * COLUMN RESIZING
   *********************************/
  const handleColumnResizeMouseDown = (e, colIndex) => {
    resizingColIndexRef.current = colIndex;
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[colIndex];
    // Add listeners to the entire document for mouse move/up
    document.addEventListener("mousemove", handleColumnResizeMouseMove);
    document.addEventListener("mouseup", handleColumnResizeMouseUp);
  };

  const handleColumnResizeMouseMove = useCallback((e) => {
    if (resizingColIndexRef.current !== null) {
      const deltaX = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + deltaX;
      setColumnWidths((prev) =>
        prev.map((width, i) =>
          i === resizingColIndexRef.current
            ? Math.max(newWidth, 40) // min width 40
            : width
        )
      );
    }
  }, []);

  const handleColumnResizeMouseUp = useCallback(() => {
    resizingColIndexRef.current = null;
    document.removeEventListener("mousemove", handleColumnResizeMouseMove);
    document.removeEventListener("mouseup", handleColumnResizeMouseUp);
  }, [handleColumnResizeMouseMove]);


  /*********************************
   * EXISTING LOGIC FOR ADDING FIELDS,
   * RENAMING FIELDS, and OVERWRITING VALUES
   *********************************/
  const getNewFieldName = () => {
    const existingFields = shotListState.length
      ? Object.keys(shotListState[0])
      : [];
    let newFieldName = "Untitled Field";

    while (existingFields.includes(newFieldName)) {
      newFieldName = newFieldName + '(1)';
    }
    return newFieldName;
  };

  const handleAddToHistory = (shotList) => {
    if(shotListHistory.length < MAX_HISTORY_LENGTH){
        setShotListHistory([...shotListHistory.slice(0, historyIndex), shotList]);
        setHistoryIndex(historyIndex + 1);
        //also add default column widths for the new fields
        setColumnWidths((prevWidths) => [...prevWidths, 150]);
    }
    else{
        setShotListHistory([...shotListHistory.slice(1, historyIndex), shotList]);
        setHistoryIndex(historyIndex);
        //also add default column widths for the new fields
        setColumnWidths((prevWidths) => [...prevWidths, 150]);
    }
  }

  // Debounced version of handleAddToHistory
const debouncedHandleAddToHistory = debounce(handleAddToHistory, 2000);


  const handleAddField = () => {
    handleAddToHistory(shotListState);
    const newFieldName = getNewFieldName();
    setShotListState((prevState) =>
      prevState.map((row) => ({
        ...row,
        [newFieldName]: "",
      }))
    );
    // Also add a default column width for this new field
    setColumnWidths((prevWidths) => [...prevWidths, 150]);
  };

  const handleAddShot = () => {
    handleAddToHistory(shotListState);
    const newRow = {};
    fields.forEach(field => {
      newRow[field] = "";
    });
    setShotListState(prevState => addIdentifierToShots([...prevState, newRow]));
    setRowHeights(prevHeights => [...prevHeights, 'auto']);
  };

  //function to delete a field
const handleDeleteField = (fieldIndex) => {
    handleAddToHistory(shotListState);
    const fieldName = fields[fieldIndex];
    setShotListState((prevState) => {
        const newState = prevState.map((row) => {
            const newRow = {};
            for (const key in row) {
                if (key !== fieldName) {
                    newRow[key] = row[key];
                }
            }
            return newRow;
        });
        return newState;
    });
    // Also remove the corresponding column width
    setColumnWidths((prevWidths) => prevWidths.filter((_, index) => index !== fieldIndex));
};

//function to delete a shot
const handleDeleteShot = (rowIndex) => {
    handleAddToHistory(shotListState);
    setShotListState((prevState) => {
        if (prevState.length === 1) {
            // If there's only one row left, clear its values instead of deleting the row
            const clearedRow = {};
            Object.keys(prevState[0]).forEach(key => {
                clearedRow[key] = "";
            });
            return addIdentifierToShots([clearedRow]);
        } else {
            // Otherwise, delete the row
            return addIdentifierToShots(prevState.filter((_, index) => index !== rowIndex));
        }
    });
    // Also remove the corresponding row height if more than one row
    setRowHeights((prevHeights) => {
        if (prevHeights.length === 1) {
            return prevHeights;
        } else {
            return prevHeights.filter((_, index) => index !== rowIndex);
        }
    });
};


  const handleFieldNameChange = (oldName, newName) => {
    debouncedHandleAddToHistory(shotListState);
    // Generate a unique new field name if the new name already exists
    let uniqueNewName = newName;
    let counter = 1;
    while (shotListState.some(row => row.hasOwnProperty(uniqueNewName))) {
      uniqueNewName = `${newName}_${counter}`;
      counter++;
    }
  
    setShotListState((prevState) =>
      prevState.map((row) => {
        if (row.hasOwnProperty(oldName)) {
          const newRow = {};
          for (const key in row) {
            if (key === oldName) {
              newRow[uniqueNewName] = row[oldName];
            } else {
              newRow[key] = row[key];
            }
          }
          return newRow;
        }
        return row;
      })
    );
  };

  const handleValueChange = (rowIndex, fieldName, newValue) => {
    debouncedHandleAddToHistory(shotListState);
    setShotListState((prevState) =>
      prevState.map((row, i) => {
        if (i === rowIndex) {
          return {
            ...row,
            [fieldName]: newValue,
          };
        }
        return row;
      })
    );
  };

  // Function to handle textarea resize
  const handleTextareaResize = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto'; // Reset height to auto to calculate the new height
    textarea.style.height = `${textarea.scrollHeight}px`; // Set the height to the scrollHeight
  };

  // function to undo the last action
    

    
    

  if (!shotListState.length) {
    return <div>No data to display.</div>;
  }

  const fields = Object.keys(shotListState[0]).filter(f => f !== "index");

  return (
    <div>
    <div className="shotlist-container">
        <br/>
        
      <table className="spreadsheet-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}> # </th>
            {fields.map((field, fieldIndex) => {
              if (field === "identifier") return null;
              return (
              <th
                key={fieldIndex}
                style={{ width: columnWidths[fieldIndex] }}
                className="spreadsheet-th"
              >
                <div className="spreadsheet-th-content">
                <input
                  type="text"
                  value={field}
                  onChange={(e) => handleFieldNameChange(field, e.target.value)}
                />
                {/* Delete Field Button */}
                <Button size='small' type='ghost' label='' onClick={()=>handleDeleteField(fieldIndex)} icon={minusIcon} className='delete-field-button' />
                
                </div>
                {/* The column resize handle */}
                <div
                  className="column-resize-handle"
                  onMouseDown={(e) =>
                    handleColumnResizeMouseDown(e, fieldIndex)
                  }
                />
              </th>
            )
            })}
            <th className="spreadsheet-add-th">
                <Button size='small' icon={plusIcon} onClick={handleAddField}/>
            </th>
          </tr>
        </thead>
        <tbody>
          {shotListState.map((row, rowIndex) => (
            <tr
            //if column youre talking about is index then increment the value by 1
            //should work automatically
              key={rowIndex}
              style={{ height: rowHeights[rowIndex] }}
              className="spreadsheet-row"
            >
              <td className="spreadsheet-index-cell">{row.identifier}</td>
              {fields.map((field, colIndex) => {
                if (field === "identifier") return null;
                return (
                <td key={colIndex} style={{ width: columnWidths[colIndex] }} className='spreadsheet-td'>
                  
                  <textarea
                    value={row[field] || ""}
                    onChange={(e) =>
                      handleValueChange(rowIndex, field, e.target.value)
                    }
                    onInput={handleTextareaResize}
                  />
                </td>
              )})}
              <td className="spreadsheet-delete-shot">
              <Button size='small' type='ghost' onClick={()=>handleDeleteShot(rowIndex)} label='' icon={minusIcon} className='delete-shot-button' />
                </td>
            </tr>
          ))}
        </tbody>
        
      </table>
       
    </div>
    <Button onClick={handleAddShot} className='shotlist-add-shot-button' size='medium' label='Add New Shot'/>
    </div>
  );
};

export default ShotListTable;
